import { API_PREFIX, API_URL, useAuthorizationMutation } from "@shared/api";
import { AppRoutes, LOCAL_STORAGE } from "@shared/constants";
import { getApplicationTelegramData, logIn } from "@shared/utils";
import { Loader } from "@widgets/Loader";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { AuthError } from "@pages/AuthError";

interface AuthProviderProps {
  children: ReactNode;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [authorization, { isLoading: authorizationIsLoading }] =
    useAuthorizationMutation();
  const [authError, setAuthError] = useState(false);
  const [authErrorCode, setAuthErrorCode] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const retryCountRef = useRef(0);
  const isAuthenticatingRef = useRef(false);
  const isRedirectingToAuthErrorRef = useRef(false);

  const handleLogin = async () => {
    if (isAuthenticatingRef.current) {
      return;
    }

    isAuthenticatingRef.current = true;

    try {
      const { data: response, error } = await authorization({
        applicationTelegramDataDto: getApplicationTelegramData(),
      });

      if (error) {
        const status = "status" in error ? error.status : null;

        if (status === 401 || status === 424 || status === 500) {
          console.error(`Authorization failed with status ${status}`);
          setAuthError(true);
          setAuthErrorCode(status);
          isAuthenticatingRef.current = false;
          isRedirectingToAuthErrorRef.current = true;
          return;
        }

        throw new Error("User authorization failed.");
      }

      if (!response || !response.data) {
        throw new Error("User authorization failed.");
      }

      logIn(response.data);
      setAuthError(false);
      setIsInitialized(true);
      retryCountRef.current = 0;
    } catch (err) {
      console.error(
        `Authorization error (attempt ${
          retryCountRef.current + 1
        }/${MAX_RETRIES}):`,
        err
      );

      if (retryCountRef.current < MAX_RETRIES - 1) {
        retryCountRef.current += 1;

        setTimeout(() => {
          isAuthenticatingRef.current = false;
          handleLogin();
        }, RETRY_DELAY);
      } else {
        console.error("All authorization attempts failed");
        setAuthError(true);
        setAuthErrorCode(null);
        isRedirectingToAuthErrorRef.current = true;
      }
    } finally {
      if (retryCountRef.current >= MAX_RETRIES - 1) {
        isAuthenticatingRef.current = false;
      }
    }
  };

  const handleLogout = async (visibilityState: DocumentVisibilityState) => {
    try {
      if (visibilityState !== "hidden") return;

      const sessionId = localStorage.getItem(LOCAL_STORAGE.SessionId) || "";
      const url = `${API_URL}${API_PREFIX}/auth/${sessionId}`;

      const blob = new Blob([JSON.stringify({ sessionId })], {
        type: "application/json",
      });

      navigator.sendBeacon(url, blob);
    } catch (err) {
      console.error(err);
    }
  };

  const handleVisibilityChange = (e: Event) => {
    const visibilityState = (e.target as Document).visibilityState;
    handleLogout(visibilityState);
  };

  useEffect(() => {
    handleLogin();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleAuthError = () => {
      if (
        isRedirectingToAuthErrorRef.current ||
        window.location.pathname.includes(AppRoutes.AUTH_ERROR)
      ) {
        return;
      }

      setAuthError(true);
      setAuthErrorCode(null);
      isRedirectingToAuthErrorRef.current = true;
    };

    window.addEventListener("auth-error", handleAuthError);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("auth-error", handleAuthError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authError) {
    return <AuthError errorCode={authErrorCode} />;
  }

  if (!isInitialized || authorizationIsLoading) {
    return <Loader />;
  }

  return <>{children}</>;
};
